import {
  PreviewCard as PreviewCardPrimitive,
  PreviewCardPopupProps,
  PreviewCardPositionerProps,
  PreviewCardRootProps,
  PreviewCardTriggerProps,
} from "@base-ui/react/preview-card"
import * as React from "react"

import Icon from "#React/ui/Icon/Icon"
import cn from "#Utils/cn"

type HoverCardProps = {
  disabled?: boolean
} & PreviewCardRootProps

export const HoverCard = ({ disabled, ...props }: HoverCardProps) => {
  return <PreviewCardPrimitive.Root {...props} {...(disabled && { open: false })} />
}

type TriggerProps = PreviewCardTriggerProps

export const Trigger = ({ delay = 200, closeDelay = 300, children, ...props }: TriggerProps) => {
  return <PreviewCardPrimitive.Trigger {...{ delay, closeDelay, children }} {...props} />
}

type ContentProps = {
  title?: string
  maxWidth?: string
  asTooltip?: boolean
  theme?: "dark" | "light"
  children?: React.ReactNode
} & Pick<PreviewCardPositionerProps, "side" | "align" | "alignOffset" | "sideOffset"> &
  Pick<PreviewCardPopupProps, "className">

export const Content = ({
  side = "bottom",
  align = "center",
  alignOffset = 0,
  sideOffset = 4,
  title,
  maxWidth = "max-w-xs",
  className,
  asTooltip = false,
  children,
  theme = "dark",
}: ContentProps) => {
  const bgColor = theme === "dark" ? "bg-gray-900" : "bg-surface-default"
  const textColor = theme === "dark" ? "text-white" : "text-content-default"

  return (
    <PreviewCardPrimitive.Portal>
      <PreviewCardPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className={cn("isolate z-1000", textColor)}
      >
        <PreviewCardPrimitive.Popup
          className={cn(
            maxWidth,
            bgColor,
            textColor,
            "ignore-on-outside-callback rounded-lg",
            { "py-2 px-3": asTooltip, "p-4": !asTooltip },
            className,
          )}
        >
          <PreviewCardPrimitive.Arrow
            className={cn(
              "flex data-[side=bottom]:-top-2 data-[side=left]:-right-2 data-[side=left]:rotate-90 data-[side=right]:-left-2 data-[side=right]:-rotate-90 data-[side=top]:-bottom-2 data-[side=top]:rotate-180",
              theme === "dark" ? "text-gray-900" : "text-surface-default",
            )}
          >
            <Icon name="fa-sharp fa-solid fa-caret-up" />
          </PreviewCardPrimitive.Arrow>
          {title && (
            <header>
              <h2 className="c-heading mb-0">{title}</h2>
            </header>
          )}
          {children}
        </PreviewCardPrimitive.Popup>
      </PreviewCardPrimitive.Positioner>
    </PreviewCardPrimitive.Portal>
  )
}

const Separator = ({ className }: { className?: string }) => (
  <hr className={cn("ignore-old-css -mx-4 border-gray-700 my-2", className)} />
)

HoverCard.Trigger = Trigger
HoverCard.Content = Content
HoverCard.Separator = Separator

export type { ContentProps, HoverCardProps, TriggerProps }

export default HoverCard
